import { createElement as h, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ContinuationDefaultViewSchema, SessionContinuationViewSchema } from "./session-continuation-types.js";
import { composerNoticeParagraphStyle, composerNoticeButtonStyle } from "./composer-notice-style.js";
import { nativeCompactButtonStyle, nativeCaptionStyle, nativeSettingsStyle, nativeSettingsCss } from "./native-settings-style.js";
const control = nativeCompactButtonStyle;
const paragraph = nativeCaptionStyle;
export function SessionContinuationCard({ sessionId, remote, locale = 'en', running = false, onEnabledChange, expanded = false, recovery = false, onCancel }) {
    const zh = locale.startsWith('zh');
    const body = recovery ? composerNoticeParagraphStyle : paragraph;
    const button = recovery ? composerNoticeButtonStyle : control;
    const [view, setView] = useState();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const [reload, setReload] = useState(0);
    const owner = useRef(0);
    useEffect(() => { onEnabledChange?.(view?.enabled === true || view?.activeTurnEnabled === true); }, [view, onEnabledChange]);
    useEffect(() => {
        const generation = ++owner.current;
        setView(undefined);
        setBusy(true);
        setError(false);
        void (async () => {
            try {
                const result = await remote?.get(sessionId);
                const parsed = result?.ok ? SessionContinuationViewSchema.safeParse(result.value) : undefined;
                if (owner.current !== generation)
                    return;
                if (!parsed?.success)
                    setError(true);
                else
                    setView(parsed.data);
            }
            catch {
                if (owner.current === generation)
                    setError(true);
            }
            finally {
                if (owner.current === generation)
                    setBusy(false);
            }
        })();
        return () => { owner.current++; };
    }, [remote, sessionId, running, reload]);
    const save = async (enabled) => {
        if (!view || !remote || busy || recovery && running)
            return;
        const generation = owner.current;
        setBusy(true);
        setError(false);
        try {
            const result = await remote.set(sessionId, view.revision, enabled);
            const parsed = result?.ok ? SessionContinuationViewSchema.safeParse(result.value) : undefined;
            if (owner.current !== generation)
                return;
            if (!parsed?.success || enabled !== null && parsed.data.enabled !== enabled) {
                setView(undefined);
                setError(true);
            }
            else
                setView(parsed.data);
        }
        catch {
            if (owner.current === generation) {
                setView(undefined);
                setError(true);
            }
        }
        finally {
            if (owner.current === generation)
                setBusy(false);
        }
    };
    const loss = h('p', { style: body }, zh
        ? '聊天及本 Session 的原生压缩使用可见历史，省略旧加密推理及内嵌摘要，同账号也适用；可能失去隐含细节。可见消息、工具关系和原历史保留，摘要由原生事务提交。不会额外发送聊天或重试。'
        : 'Chat and native compaction in this Session use visible history, omitting old encrypted reasoning and embedded summaries even on the same account; implicit details may be lost. Visible messages, tool relations and source history remain intact; native transactions commit summaries. No extra chat sending or retries.');
    const unavailable = error ? h('p', { role: 'alert', style: body }, 'COPILOT_CONTINUATION_STATUS_UNAVAILABLE', h('button', { type: 'button', style: button, disabled: busy || recovery && running,
        onClick: () => setReload(value => value + 1) }, zh ? '重试读取' : 'Retry read')) : null;
    if (recovery)
        return h('div', { 'data-copilot-continuation-recovery': '' }, !view && !error ? h('p', { role: 'status', style: body }, zh ? '正在读取降级续聊策略…' : 'Reading continuation policy…') : null, view?.enabled ? h('p', { role: 'status', style: body }, zh
            ? '本 Session 已开启降级续聊，请另行使用原生重试。如果仍被拒绝，请查看技术诊断或新建会话；不会重复授权或自动重试。'
            : 'Continuation is already on for this Session; use native Retry separately. If rejection persists, review technical diagnostics or start a new conversation. No repeated authorization or automatic retry.') : view ? h('div', null, loss, h('p', { style: body }, zh
            ? '开启后在本 Session 持续生效，跨账号和重启保留，直到关闭。开启即同意上述损失；之后请另行使用原生重试。'
            : 'Enabling applies to this Session across accounts and restarts until disabled. This consents to the loss above; use native Retry separately afterwards.'), h('button', { type: 'button', style: button, disabled: busy || running, onClick: () => void save(true) }, zh ? '开启降级续聊' : 'Enable visible-history continuation')) : null, unavailable, h('button', { type: 'button', style: button, disabled: busy || running, onClick: onCancel }, zh ? '取消' : 'Cancel'));
    return h('details', { open: expanded || undefined, 'data-copilot-continuation-settings': '',
        'data-copilot-native-ui': true, style: nativeSettingsStyle }, h('style', null, nativeSettingsCss), h('summary', { style: { cursor: 'pointer' } }, zh ? '降级续聊' : 'Visible-history continuation', ' · ', !view ? zh ? '状态待确认' : 'Unknown' : view.enabled ? zh ? '开启' : 'On'
        : view.nextTurnAuthorized ? zh ? '仅下一轮' : 'Next turn only' : zh ? '关闭' : 'Off'), loss, h('label', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } }, zh ? '本 Session 默认策略' : 'Session policy', h('select', { style: control, disabled: busy || !view, value: view?.source === 'default' ? 'default' : view?.enabled ? 'on' : 'off',
        onChange: (event) => void save(event.currentTarget.value === 'default' ? null : event.currentTarget.value === 'on') }, [['default', zh ? '沿用创建时默认' : 'Use creation-time default'], ['on', zh ? '开启' : 'On'], ['off', zh ? '关闭' : 'Off']].map(([value, label]) => h('option', { key: value, value, style: { background: 'Canvas', color: 'CanvasText' } }, label)))), view?.activeTurnEnabled !== undefined && view.activeTurnEnabled !== view.enabled
        ? h('p', { role: 'status', style: paragraph }, zh ? '当前轮保持原策略，变更从下一轮生效。' : 'The active turn keeps its policy; changes apply next turn.') : null, unavailable);
}
export function ContinuationDefaultCard({ remote, locale = 'en', onSaved }) {
    const zh = locale.startsWith('zh');
    const [view, setView] = useState();
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);
    const lifetime = useRef(0);
    useEffect(() => {
        const generation = ++lifetime.current;
        setBusy(true);
        setError(false);
        setView(undefined);
        void (async () => {
            try {
                const result = await remote?.defaults?.();
                const parsed = result?.ok ? ContinuationDefaultViewSchema.safeParse(result.value) : undefined;
                if (generation !== lifetime.current)
                    return;
                if (parsed?.success)
                    setView(parsed.data);
                else
                    setError(true);
            }
            catch {
                if (generation === lifetime.current)
                    setError(true);
            }
            finally {
                if (generation === lifetime.current)
                    setBusy(false);
            }
        })();
        return () => { lifetime.current++; };
    }, [remote, reload]);
    const save = async (enabled) => {
        if (!view || !remote?.setDefault || busy)
            return;
        const generation = lifetime.current;
        setBusy(true);
        setError(false);
        try {
            const result = await remote.setDefault(view.revision, enabled);
            const parsed = result.ok ? ContinuationDefaultViewSchema.safeParse(result.value) : undefined;
            if (generation !== lifetime.current)
                return;
            if (parsed?.success) {
                setView(parsed.data);
                onSaved?.();
            }
            else {
                setView(undefined);
                setError(true);
            }
        }
        catch {
            if (generation === lifetime.current) {
                setView(undefined);
                setError(true);
            }
        }
        finally {
            if (generation === lifetime.current)
                setBusy(false);
        }
    };
    return h('details', { 'data-copilot-native-ui': true, style: nativeSettingsStyle }, h('style', null, nativeSettingsCss), h('summary', null, zh ? '新 Session 降级默认' : 'New Session continuation default'), h('label', null, h('input', { type: 'checkbox', checked: view?.enabled ?? false, disabled: busy || !view,
        onChange: (event) => void save(event.currentTarget.checked) }), zh ? '新 Session 默认开启降级续聊' : 'Enable visible-history continuation for new Sessions'), h('p', { style: paragraph }, zh
        ? '开启即同意新 Session 的聊天及原生压缩省略旧加密推理及内嵌摘要，可能丢失隐藏细节，同账号也适用。已有 Session 和继承历史不自动授权，原历史和工具关系保留。'
        : 'Enabling consents to omitting prior encrypted reasoning and embedded summaries in new Session chat and native compaction, even on the same account; hidden details may be lost. Existing Sessions and seeded histories are not automatically enrolled. Source history and tool relations are preserved.'), error ? h('p', { role: 'alert' }, 'COPILOT_CONTINUATION_STATUS_UNAVAILABLE', h('button', { type: 'button', style: control, onClick: () => setReload(value => value + 1) }, zh ? '重试' : 'Retry')) : null);
}
function SwitchConfirmation({ consent, sessionId, remote, locale, done, cancel }) {
    const zh = locale.startsWith('zh');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const active = useRef(true);
    useEffect(() => { active.current = true; return () => { active.current = false; consent.resolve(false); cancel(); }; }, [consent]);
    const choose = async (choice) => {
        if (busy)
            return;
        if (choice === 'off') {
            done(true);
            return;
        }
        setBusy(true);
        setError(false);
        try {
            const result = await remote.set(sessionId, consent.view.revision, true);
            const parsed = result?.ok ? SessionContinuationViewSchema.safeParse(result.value) : undefined;
            if (!active.current)
                return;
            if (!parsed?.success || !parsed.data.enabled)
                setError(true);
            else
                done(true);
        }
        catch {
            if (active.current)
                setError(true);
        }
        finally {
            if (active.current)
                setBusy(false);
        }
    };
    return h('section', { role: 'group', 'aria-label': zh ? '切换账号前确认' : 'Before switching accounts',
        style: { border: control.border, borderRadius: 6, padding: 10, marginBlock: 10 } }, h('strong', null, zh ? '此 Session 的降级续聊未开启' : 'Continuation is off for this Session'), h('p', { style: paragraph }, zh
        ? '切换账号后，旧加密推理可能被拒绝，但不一定失败。降级每轮省略旧推理及内嵌摘要，保留可见消息与工具记录；可能损失隐含细节。开启后持续生效，直到关闭。不会自动重试。'
        : 'After switching, old encrypted reasoning may be rejected; failure is not certain. Continuation omits old reasoning and embedded summaries each turn while keeping visible messages and tools. Implicit details may be lost. Enabling persists until disabled. No automatic retry.'), h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } }, h('button', { type: 'button', style: control, disabled: busy, onClick: () => void choose('session') }, zh ? '持续开启并切换' : 'Enable for Session and switch'), h('button', { type: 'button', style: control, disabled: busy, onClick: () => void choose('off') }, zh ? '保持关闭并切换' : 'Keep off and switch'), h('button', { type: 'button', style: control, disabled: busy, onClick: () => done(false) }, zh ? '取消' : 'Cancel')), error ? h('p', { role: 'alert', style: paragraph }, zh
        ? '授权保存未确认，账号未切换。取消后重新读取状态再试。'
        : 'Authorization was not confirmed; account not switched. Cancel and read current status before trying again.') : null);
}
export function useSessionContinuationSwitch({ sessionId, remote, locale = 'en', changesAccount, running = false }) {
    const [consent, setConsent] = useState();
    const pending = useRef();
    const generation = useRef(0);
    useEffect(() => {
        setConsent(undefined);
        return () => { generation.current++; pending.current?.resolve(false); pending.current = undefined; };
    }, [sessionId, remote]);
    const beforeAccountChange = useCallback(async (accountId, signal) => {
        const owner = generation.current;
        if (signal?.aborted)
            return false;
        const changed = await changesAccount(accountId);
        if (owner !== generation.current || signal?.aborted)
            return false;
        if (!changed)
            return true;
        const result = await remote?.get(sessionId);
        if (owner !== generation.current || signal?.aborted)
            return false;
        const parsed = result?.ok ? SessionContinuationViewSchema.safeParse(result.value) : undefined;
        if (!parsed?.success)
            throw new Error('COPILOT_CONTINUATION_STATUS_UNAVAILABLE');
        if (parsed.data.enabled || parsed.data.nextTurnAuthorized)
            return true;
        pending.current?.resolve(false);
        return new Promise(resolve => {
            let settled = false;
            const next = { view: parsed.data, resolve: approved => {
                    if (settled)
                        return;
                    settled = true;
                    signal?.removeEventListener('abort', abort);
                    resolve(approved);
                } };
            const abort = () => {
                next.resolve(false);
                if (pending.current === next) {
                    pending.current = undefined;
                    setConsent(undefined);
                }
            };
            pending.current = next;
            signal?.addEventListener('abort', abort, { once: true });
            if (signal?.aborted) {
                abort();
                return;
            }
            setConsent(next);
        });
    }, [remote, sessionId, changesAccount]);
    const done = (approved) => {
        const current = pending.current;
        pending.current = undefined;
        current?.resolve(approved);
        setConsent(undefined);
    };
    return { beforeAccountChange, content: h(Fragment, null, consent && remote ? h(SwitchConfirmation, { key: consent.view.revision, consent, sessionId, remote, locale, done,
            cancel: () => { if (pending.current === consent)
                done(false); } }) : null, h(SessionContinuationCard, { sessionId, remote, locale, running })) };
}
//# sourceMappingURL=session-continuation-ui.js.map